/**
 * Rich-text editor for ICS-202 boxes 5, 7, 11, 12 and 13: the report editor,
 * with images uploaded to the incident's files and the AI's inline citations
 * kept as clickable chips that open the cited document at its page.
 */
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import TiptapRichTextEditor, {
  type LibraryImage,
} from 'components/DesignSystems/New/TiptapRichTextEditor';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { fetchDownloadSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { useFetchCitationFileUrl } from 'components/DisasterGPT/hooks/useFetchCitationFileUrl';
import { safeWebUrl } from 'components/DisasterGPT/fieldDraft/ClickableFieldCitations';
import { useActualIncidentId } from '../hooks/commonHooks';
import { useFormPhotoFiles } from '../formPhotos/useFormPhotoFiles';
import { CitationChip } from './citationChipExtension';
import {
  FILE_IMAGE_SCHEME,
  richImageFileIds,
  toEditorHtml,
  toStoredHtml,
} from './ics202RichFields';

const EXTENSIONS = [CitationChip];

type OpenFile = {
  fileUrl: string;
  fileName: string;
  mimetype: string;
  page?: number;
};

export type Ics202RichTextFieldProps = {
  id: string;
  value: string | null | undefined;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

export const Ics202RichTextField: React.FC<Ics202RichTextFieldProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  placeholder,
}) => {
  const incidentId = useActualIncidentId();
  const fetchFileUrl = useFetchCitationFileUrl();
  const [openFile, setOpenFile] = useState<OpenFile | null>(null);
  // The editor re-serializes content it is handed (and swaps image URLs); only
  // an edit the user starts after the value last changed counts as a change.
  const touched = useRef(false);
  const seenValue = useRef(value);
  if (seenValue.current !== value) {
    seenValue.current = value;
    touched.current = false;
  }

  const html = useMemo(() => toEditorHtml(value), [value]);
  const imageIds = useMemo(() => richImageFileIds(html), [html]);
  const { data: files } = useFormPhotoFiles(incidentId, imageIds);
  const imageLibrary = useMemo<LibraryImage[] | undefined>(() => {
    if (!files?.size) return undefined;
    return [...files.values()].map((file) => ({
      id: file.id,
      fileName: file.fileName ?? '',
      fileUrl: `${FILE_IMAGE_SCHEME}${file.id}`,
      mimetype: file.mimetype ?? '',
      size: '',
      createdAt: null,
      updatedAt: null,
      signedUrl: file.signedUrl ?? undefined,
      expiresAt: null,
    }));
  }, [files]);

  const handleChange = useCallback(
    (next: string) => {
      if (!touched.current) return;
      onChange(toStoredHtml(next));
    },
    [onChange]
  );

  const uploadImage = useCallback(
    async (picked: File[]) => {
      if (!incidentId) throw new Error('No incident');
      try {
        const uploaded = await uploadFilesToBackend({
          files: picked,
          workspaceId: incidentId,
          ragIndex: false,
        });
        touched.current = true;
        return await Promise.all(
          uploaded
            .filter((file) => !!file.id)
            .map(async (file) => ({
              id: file.id,
              signedUrl: await fetchDownloadSignedUrl(file.fileUrl),
            }))
        );
      } catch (error) {
        toast.error('The image could not be uploaded.');
        throw error;
      }
    },
    [incidentId]
  );

  const handleCitationClick = async (event: React.MouseEvent) => {
    const chip = (event.target as HTMLElement).closest?.(
      '[data-cite]'
    ) as HTMLElement | null;
    if (!chip) return;
    const fileId = chip.getAttribute('data-file-id');
    const url = safeWebUrl(chip.getAttribute('data-url') ?? undefined);
    if (!fileId && !url) return;
    event.preventDefault();
    event.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const file = await fetchFileUrl(fileId as string);
      const page = Number(chip.getAttribute('data-page'));
      setOpenFile({
        fileUrl: file.url,
        fileName: file.fileName,
        mimetype: file.mimetype,
        page: page > 0 ? page : undefined,
      });
    } catch {
      toast.error('Could not open the cited document.');
    }
  };

  const markTouched = () => {
    touched.current = true;
  };

  return (
    <div
      id={id}
      className="ics202-rich-text"
      onClickCapture={(event) => void handleCitationClick(event)}
      onKeyDownCapture={markTouched}
      onPasteCapture={markTouched}
      onDropCapture={markTouched}
      onMouseDownCapture={(event) => {
        if ((event.target as HTMLElement).closest?.('button')) markTouched();
      }}
    >
      <TiptapRichTextEditor
        className="tiptap-editor--notes"
        value={html}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        minHeight="120px"
        imageLibrary={imageLibrary}
        onClickUploadImage={incidentId ? uploadImage : undefined}
        extensions={EXTENSIONS}
      />
      {openFile ? (
        <StylishViewFileDialog
          show
          onClose={() => setOpenFile(null)}
          fileUrl={openFile.fileUrl}
          fileName={openFile.fileName}
          page={openFile.page}
          mimetype={openFile.mimetype}
        />
      ) : null}
    </div>
  );
};

export default Ics202RichTextField;
